<template>
  <div class="agreement-cancellations">
    <PageTitle hideMarginLeft>Сторно договора в УСП</PageTitle>

    <div class="agreement-cancellations__filters tw-flex tw-flex-wrap tw-items-end tw-gap-3 tw-mb-4">
      <CustomSelect
        :value="filters.sbs_project_id"
        :options="sbsProjects"
        label="ЖК"
        option-value-key="sbs_project_id"
        option-label-key="zk_name"
        placeholder="Все ЖК"
        clearable
        style="min-width: 220px"
        @change="onSbsProjectChange"
      />

      <CustomSelect
        :value="filters.project_type_id"
        :options="projectTypeOptions"
        label="Тип проекта"
        option-value-key="project_type_id"
        option-label-key="project_type_name"
        placeholder="Все типы"
        clearable
        style="min-width: 200px"
        @change="(v) => (filters.project_type_id = v)"
      />

      <CustomSelect
        :value="filters.project_entrance_id"
        :options="entranceOptions"
        label="Подъезд / блок"
        option-value-key="project_entrance_id"
        option-label-key="label"
        placeholder="Все"
        clearable
        style="min-width: 200px"
        @change="(v) => (filters.project_entrance_id = v)"
      />

      <CustomDateRange
        :value="filters.dateRange"
        label="Дата договора"
        @change_range="onDateRangeChange"
      />

      <CustomSelect
        :value="filters.has_cancel"
        :options="HAS_CANCEL_OPTIONS"
        label="Расторжение в 1С"
        option-value-key="value"
        option-label-key="label"
        placeholder="Все"
        clearable
        style="min-width: 200px"
        @change="(v) => (filters.has_cancel = v)"
      />

      <CustomSelect
        v-if="hasUspStornoGrant"
        :value="uspApiUrl"
        :options="USP_BASE_OPTIONS"
        label="База УСП"
        option-value-key="value"
        option-label-key="label"
        placeholder="Выберите базу"
        clearable
        style="min-width: 180px"
        @change="(v) => (uspApiUrl = v)"
      />
    </div>

    <q-linear-progress v-if="isLoading" indeterminate color="primary" class="tw-mb-2" />

    <CustomTable
      title="Договоры подряда в 1С"
      :rowsData="rowsData"
      :columns="visibleColumns"
      :loading="isLoading"
      :small="true"
      :show-search="true"
      :custom-body="true"
      :sticky-header="true"
      :show-pagination="totalCount > LIMIT"
      :pages-number="Math.ceil(totalCount / LIMIT)"
      :count="totalCount"
      :on-pagination-change="onPageChange"
    >
      <template #customBody="props">
        <q-tr>
          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.zk_name || "—" }}
          </custom-table-cell>
          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.project_type_name || "—" }}
          </custom-table-cell>
          <custom-table-cell align="left" vertical-align="center">
            <div>{{ entranceLine(props.row) }}</div>
            <div v-if="blockLine(props.row)" class="agreement-cancellations__block">
              {{ blockLine(props.row) }}
            </div>
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center">
            {{ props.row.project_id || "—" }}
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center">
            <q-badge
              :color="props.row.project_status_code === 'CANCELLED' ? 'negative' : 'grey'"
              :label="props.row.project_status_name || props.row.project_status_code || '—'"
            />
          </custom-table-cell>
          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.agreement_num || "—" }}
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center">
            {{ formatDate(props.row.document_date) }}
          </custom-table-cell>
          <custom-table-cell align="left" vertical-align="center">
            <span class="agreement-cancellations__guid">{{ props.row.guid || "—" }}</span>
          </custom-table-cell>
          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.cancel_agreement_num || "—" }}
          </custom-table-cell>

          <custom-table-cell v-if="hasUspStornoGrant" align="center" vertical-align="center">
            <q-badge v-if="isTrue(props.row.usp_log_complete)" color="positive" :label="uspLogLabel(props.row)" />
            <q-badge v-else-if="Number(props.row.usp_works_count) > 0" color="warning" label="Лог неполный" />
            <q-badge v-else color="grey" label="Нет лога" />
          </custom-table-cell>
          <custom-table-cell v-if="hasUspStornoGrant" align="center" vertical-align="center">
            <q-badge
              v-if="isTrue(props.row.usp_storno_sent)"
              color="positive"
              :label="`Сторно ${props.row.usp_storno_works_count || 0}`"
            />
            <q-badge v-else color="grey" label="Не отправляли" />
          </custom-table-cell>
          <custom-table-cell v-if="hasUspStornoGrant" align="center" vertical-align="center">
            <div class="tw-flex tw-items-center tw-justify-center tw-gap-2">
              <q-btn
                v-if="canAuto(props.row)"
                dense
                unelevated
                color="deep-orange-7"
                no-caps
                icon="send"
                label="Сторно работ"
                :loading="isBusy(props.row, 'works')"
                @click="openWorksDialog(props.row)"
              />
              <q-btn
                v-else
                dense
                outline
                color="primary"
                no-caps
                label="JSON вручную"
                :loading="isBusy(props.row, 'manual')"
                @click="openManualDialog(props.row)"
              />
            </div>
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>

    <NotFoundText v-if="!isLoading && !rowsData.length" />

    <q-dialog v-model="worksDialog.open" maximized>
      <q-card>
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Сторно работ в УСП</div>
          <q-space />
          <q-btn
            label="Отправить в УСП"
            color="deep-orange-7"
            no-caps
            dense
            icon="send"
            :disable="!selectedWorks.length"
            :loading="worksDialog.sending"
            class="q-mr-sm"
            @click="confirmSendSelected"
          />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>
        <q-card-section class="q-pt-sm q-pb-none text-grey-8" style="font-size: 13px">
          {{ worksDialog.row?.agreement_num }} · project_id {{ worksDialog.row?.project_id }}
          · к отправке Value = 0
        </q-card-section>
        <q-card-section>
          <q-banner dense rounded class="bg-orange-1 text-orange-9" style="font-size: 13px">
            {{ USP_STORNO_WARNING }}
          </q-banner>
        </q-card-section>
        <q-card-section>
          <q-table
            :rows="worksDialog.rows"
            :columns="uspWorkColumns"
            :loading="worksDialog.loading"
            row-key="work_guid"
            selection="multiple"
            v-model:selected="selectedWorks"
            flat
            dense
            wrap-cells
          >
            <template #body-cell-location_text="cellProps">
              <q-td :props="cellProps">
                <span>{{ cellProps.row.location_text || "на текущих этажах не найдено" }}</span>
                <q-badge v-if="isTrue(cellProps.row.is_moved)" color="warning" class="q-ml-sm" label="Guid на другом проекте" />
              </q-td>
            </template>
          </q-table>
        </q-card-section>
      </q-card>
    </q-dialog>

    <q-dialog v-model="manualDialog.open">
      <q-card style="min-width: 720px; max-width: 90vw">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Ручной JSON CreateContract</div>
          <q-space />
          <q-btn
            label="Отправить в УСП"
            color="deep-orange-7"
            no-caps
            dense
            icon="send"
            :loading="manualDialog.sending"
            class="q-mr-sm"
            @click="confirmSendManual"
          />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>
        <q-card-section class="q-pt-sm q-pb-none text-grey-8" style="font-size: 13px">
          Нет полного лога CreateContract. Соберите JSON по базе УСП. Value в каждой работе будет 0.
        </q-card-section>
        <q-card-section>
          <q-banner dense rounded class="bg-orange-1 text-orange-9" style="font-size: 13px">
            {{ USP_STORNO_WARNING }}
          </q-banner>
        </q-card-section>
        <q-card-section>
          <q-input
            v-model="manualDialog.text"
            type="textarea"
            autogrow
            outlined
            input-style="font-family: monospace; font-size: 12px; min-height: 280px"
          />
        </q-card-section>
      </q-card>
    </q-dialog>

    <q-dialog v-model="jsonPreview.open">
      <q-card style="min-width: 720px; max-width: 90vw">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">JSON отправки в УСП</div>
          <q-space />
          <q-btn
            label="Отправить в УСП"
            color="deep-orange-7"
            no-caps
            dense
            icon="send"
            :loading="jsonPreview.sending"
            class="q-mr-sm"
            @click="confirmSendPreview"
          />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>
        <q-card-section class="q-pt-sm q-pb-none text-grey-8" style="font-size: 13px">
          Превью CreateContract, Value = 0. В УСП ещё не уходило.
        </q-card-section>
        <q-card-section>
          <q-banner dense rounded class="bg-orange-1 text-orange-9" style="font-size: 13px">
            {{ USP_STORNO_WARNING }}
          </q-banner>
        </q-card-section>
        <q-card-section>
          <q-input
            v-model="jsonPreview.text"
            type="textarea"
            autogrow
            outlined
            readonly
            input-style="font-family: monospace; font-size: 12px; min-height: 280px; max-height: 60vh"
          />
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import {
  getAgreementCancellations,
  getAgreementCancellationSbsProjects,
  getAgreementCancelUspStornoWorks,
  sendAgreementCancelUspStorno,
  buildAgreementCancelUspStorno,
  HAS_CANCEL_OPTIONS,
  USP_BASE_OPTIONS,
  USP_STORNO_GRANT,
  USP_STORNO_WARNING,
  agreementCancellationColumns,
  uspWorkColumns,
} from "./services";
import { getHasGrant } from "@/views/ProjectsEntry/services";
import PageTitle from "@/components/common/PageTitle.vue";
import NotFoundText from "@/components/common/NotFoundText";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomSelect from "@/components/form/CustomSelect";
import CustomDateRange from "@/components/common/CustomDateRange";

export default {
  name: "ProjectAgreementCancellations",
  components: {
    PageTitle,
    NotFoundText,
    CustomTable,
    CustomTableCell,
    CustomSelect,
    CustomDateRange,
  },
  data() {
    return {
      rowsData: [],
      sbsProjects: [],
      isLoading: false,
      page: 1,
      totalCount: 0,
      LIMIT: 50,
      hasUspStornoGrant: false,
      uspApiUrl: null,
      busyKey: "",
      filters: {
        sbs_project_id: null,
        project_type_id: null,
        project_entrance_id: null,
        dateRange: { from: "", to: "" },
        has_cancel: null,
      },
      selectedWorks: [],
      worksDialog: {
        open: false,
        row: null,
        rows: [],
        loading: false,
        sending: false,
      },
      jsonPreview: {
        open: false,
        text: "",
        sending: false,
      },
      manualDialog: {
        open: false,
        row: null,
        text: "",
        sending: false,
      },
      HAS_CANCEL_OPTIONS,
      USP_BASE_OPTIONS,
      USP_STORNO_WARNING,
      agreementCancellationColumns,
      uspWorkColumns,
    };
  },
  computed: {
    visibleColumns() {
      if (this.hasUspStornoGrant) return this.agreementCancellationColumns;
      return this.agreementCancellationColumns.filter(
        (col) => !["usp_works_count", "usp_storno_sent", "usp_actions"].includes(col.name)
      );
    },
    projectTypeOptions() {
      const map = new Map();
      for (const row of this.filterSourceRows()) {
        for (const item of this.asArray(row.project_types)) {
          const id = Number(item?.project_type_id);
          if (!Number.isFinite(id) || map.has(id)) continue;
          map.set(id, {
            project_type_id: id,
            project_type_name: item.project_type_name || String(id),
          });
        }
      }
      return [...map.values()].sort((a, b) =>
        String(a.project_type_name).localeCompare(String(b.project_type_name), "ru")
      );
    },
    entranceOptions() {
      const map = new Map();
      for (const row of this.filterSourceRows()) {
        for (const item of this.asArray(row.entrances)) {
          const id = Number(item?.project_entrance_id);
          if (!Number.isFinite(id) || map.has(id)) continue;
          const entrance = item.entrance == null || item.entrance === "" ? "" : String(item.entrance);
          const block = String(item.block_name || "").trim();
          const entranceLabel = entrance ? `Подъезд ${entrance}` : "—";
          map.set(id, {
            project_entrance_id: id,
            label: block ? `${entranceLabel} / Блок ${block}` : entranceLabel,
          });
        }
      }
      return [...map.values()].sort((a, b) => String(a.label).localeCompare(String(b.label), "ru"));
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.page = 1;
        this.loadData();
      },
    },
  },
  async mounted() {
    this.hasUspStornoGrant = (await getHasGrant(USP_STORNO_GRANT)) === true;
    await Promise.all([this.loadData(), this.loadSbsProjects()]);
  },
  methods: {
    isTrue(val) {
      return val === true || val === 1 || val === "1" || val === "t" || val === "true";
    },
    asArray(val) {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      if (typeof val === "string") {
        try {
          const parsed = JSON.parse(val);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      return [];
    },
    filterSourceRows() {
      if (!this.filters.sbs_project_id) return this.sbsProjects;
      return this.sbsProjects.filter(
        (row) => Number(row.sbs_project_id) === Number(this.filters.sbs_project_id)
      );
    },
    blockLine(row) {
      const block = String(row?.block_name || "").trim();
      if (!block) return "";
      return /^блок\b/i.test(block) ? block : `Блок ${block}`;
    },
    entranceLine(row) {
      const entrance = String(row?.entrance || "").trim();
      if (!entrance) return "—";
      if (/^подъезд\b/i.test(entrance)) return entrance;
      return `Подъезд ${entrance}`;
    },
    onSbsProjectChange(v) {
      this.filters.sbs_project_id = v;
      this.filters.project_type_id = null;
      this.filters.project_entrance_id = null;
    },
    canAuto(row) {
      return this.isTrue(row?.usp_can_auto);
    },
    uspLogLabel(row) {
      const n = Number(row?.usp_works_remaining);
      const total = Number(row?.usp_works_count) || 0;
      if (Number.isFinite(n) && n >= 0) {
        return n === 1 ? "1 работа" : `${n} из ${total}`;
      }
      return total === 1 ? "1 работа" : `${total} работ`;
    },
    isBusy(row, action) {
      return this.busyKey === `${action}:${row?.contractor_agreement_list_id}`;
    },
    requireUspUrl() {
      if (this.uspApiUrl) return true;
      this.$q.notify({ type: "warning", message: "Выберите базу УСП" });
      return false;
    },
    unwrapPayload(res) {
      if (!res || typeof res !== "object") return res;
      if (res.Success === false || res.success === false) return res;
      if (res.data && typeof res.data === "object") return res.data;
      return res;
    },
    formatSpMessage(res) {
      const raw = res?.Message || res?.message || "";
      return String(raw).replace(/^\{|\}$/g, "") || "Ошибка";
    },
    onDateRangeChange(range) {
      this.filters.dateRange = range;
    },
    onPageChange(page) {
      this.page = page;
      this.loadData();
    },
    toIsoDate(val) {
      if (!val) return null;
      const parts = String(val).split(".");
      if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
      return val;
    },
    buildParams() {
      const params = { limit: this.LIMIT, page: this.page };
      if (this.filters.sbs_project_id) params.sbs_project_id = this.filters.sbs_project_id;
      if (this.filters.dateRange?.from) params.date_from = this.toIsoDate(this.filters.dateRange.from);
      if (this.filters.dateRange?.to) params.date_to = this.toIsoDate(this.filters.dateRange.to);
      if (this.filters.has_cancel === "0" || this.filters.has_cancel === "1") {
        params.has_cancel = this.filters.has_cancel;
      }
      if (this.filters.project_type_id) params.project_type_id = this.filters.project_type_id;
      if (this.filters.project_entrance_id) params.project_entrance_id = this.filters.project_entrance_id;
      return params;
    },
    async loadData() {
      this.isLoading = true;
      const res = await getAgreementCancellations(this.buildParams());
      this.isLoading = false;
      if (!res) return;
      this.rowsData = res.data || [];
      this.totalCount = res.total_count;
    },
    async loadSbsProjects() {
      const res = await getAgreementCancellationSbsProjects();
      if (!res) return;
      this.sbsProjects = res;
    },
    async openWorksDialog(row) {
      if (!this.requireUspUrl()) return;
      this.busyKey = `works:${row.contractor_agreement_list_id}`;
      this.worksDialog = { open: true, row, rows: [], loading: true, sending: false };
      this.selectedWorks = [];
      try {
        const rows = await getAgreementCancelUspStornoWorks({
          contractor_agreement_list_id: row.contractor_agreement_list_id,
        });
        this.worksDialog.rows = Array.isArray(rows) ? rows : [];
        if (!this.worksDialog.rows.length) {
          this.$q.notify({
            type: "warning",
            message: "Нет работ для сторно (лог неполный или все уже сторнированы с этой страницы)",
          });
        }
      } catch (e) {
        this.$q.notify({
          type: "negative",
          message: e?.response?.data?.error || e?.message || "Не удалось загрузить работы",
        });
      } finally {
        this.worksDialog.loading = false;
        this.busyKey = "";
      }
    },
    openManualDialog(row) {
      if (!this.requireUspUrl()) return;
      const template = {
        Function: "CreateContract",
        ProjectGuid: row.usp_project_guid || "",
        Works: [
          {
            Guid: "",
            Value: 0,
            VAT: 16,
            ContractGuid: row.guid || "",
          },
        ],
      };
      this.manualDialog = {
        open: true,
        row,
        text: JSON.stringify(template, null, 2),
        sending: false,
      };
    },
    async confirmSendSelected() {
      const n = this.selectedWorks.length;
      if (!n) {
        this.$q.notify({ type: "warning", message: "Выберите работы" });
        return;
      }
      if (!this.requireUspUrl()) return;
      const row = this.worksDialog.row;
      if (!row) return;

      const workGuids = this.selectedWorks.map((w) => w.work_guid).filter(Boolean);
      this.worksDialog.sending = true;
      try {
        const raw = await buildAgreementCancelUspStorno({
          contractor_agreement_list_id: row.contractor_agreement_list_id,
          api_url: this.uspApiUrl,
          work_guids: workGuids,
        });
        const payload = this.unwrapPayload(raw);
        if (payload?.Success === false || payload?.success === false) {
          this.$q.notify({
            type: "negative",
            message: this.formatSpMessage(payload),
          });
          return;
        }
        if (!payload || payload.Function !== "CreateContract") {
          this.$q.notify({
            type: "negative",
            message: this.formatSpMessage(payload) || "Не удалось собрать JSON",
          });
          return;
        }
        this.jsonPreview = {
          open: true,
          text: JSON.stringify(payload, null, 2),
          sending: false,
        };
      } catch (e) {
        this.$q.notify({
          type: "negative",
          message: e?.response?.data?.error || e?.message || "Не удалось собрать JSON",
        });
      } finally {
        this.worksDialog.sending = false;
      }
    },
    confirmSendPreview() {
      const n = this.selectedWorks.length;
      this.$q
        .dialog({
          title: "Отправить сторно в УСП",
          message: `Будет отправлен CreateContract, Value = 0, работ: ${n}.\n\n${USP_STORNO_WARNING}`,
          cancel: { label: "Отмена", flat: true, noCaps: true },
          ok: { label: "Отправить", color: "deep-orange-7", unelevated: true, noCaps: true },
          persistent: true,
        })
        .onOk(() => this.sendSelected());
    },
    confirmSendManual() {
      this.$q
        .dialog({
          title: "Отправить ручной JSON в УСП",
          message: `Value в работах будет 0. ${USP_STORNO_WARNING}`,
          cancel: { label: "Отмена", flat: true, noCaps: true },
          ok: { label: "Отправить", color: "deep-orange-7", unelevated: true, noCaps: true },
          persistent: true,
        })
        .onOk(() => this.sendManual());
    },
    async sendSelected() {
      const row = this.worksDialog.row;
      if (!row || !this.requireUspUrl()) return;
      this.worksDialog.sending = true;
      this.jsonPreview.sending = true;
      const workGuids = this.selectedWorks.map((w) => w.work_guid).filter(Boolean);
      await this.sendStorno(row, { work_guids: workGuids });
      this.worksDialog.sending = false;
      this.jsonPreview.sending = false;
    },
    async sendManual() {
      const row = this.manualDialog.row;
      if (!row || !this.requireUspUrl()) return;
      let parsed;
      try {
        parsed = JSON.parse(this.manualDialog.text);
      } catch {
        this.$q.notify({ type: "negative", message: "Некорректный JSON" });
        return;
      }
      this.manualDialog.sending = true;
      await this.sendStorno(row, { request_json: parsed });
      this.manualDialog.sending = false;
    },
    async sendStorno(row, extra) {
      this.busyKey = `send:${row.contractor_agreement_list_id}`;
      try {
        const raw = await sendAgreementCancelUspStorno({
          contractor_agreement_list_id: row.contractor_agreement_list_id,
          api_url: this.uspApiUrl,
          ...extra,
        });
        const payload = this.unwrapPayload(raw);
        if (payload?.Success === false || payload?.success === false) {
          const report = payload?.Report ? ` ${payload.Report}` : "";
          this.$q.notify({
            type: "negative",
            message: `${this.formatSpMessage(payload)}${report}`,
          });
          return;
        }
        this.$q.notify({
          type: "positive",
          message: payload?.Message
            ? this.formatSpMessage(payload)
            : `Отправлено в УСП (${payload?.SentCount || extra?.work_guids?.length || 0} работ)`,
        });
        this.worksDialog.open = false;
        this.manualDialog.open = false;
        this.jsonPreview.open = false;
        await this.loadData();
      } catch (e) {
        this.$q.notify({
          type: "negative",
          message: e?.response?.data?.error || e?.message || "Ошибка отправки в УСП",
        });
      } finally {
        this.busyKey = "";
      }
    },
    formatDate(val) {
      if (!val) return "—";
      const match = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) return `${match[3]}.${match[2]}.${match[1]}`;
      const d = new Date(val);
      if (isNaN(d.getTime())) return val;
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
    },
  },
};
</script>

<style scoped>
.agreement-cancellations__guid {
  font-size: 12px;
  word-break: break-all;
}
.agreement-cancellations__block {
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.2;
  color: #616161;
}
</style>
